import { Link, useNavigate, useLocation } from 'react-router-dom';

const LOGO_URL = "https://customer-assets-7cd3h4nn.emergentagent.net/job_regulated-payments/artifacts/nnv85px6_Easysend-logo-removebg.png";

const footerLinks = {
  platform: [
    { name: 'Dashboard', href: '#platform' },
    { name: 'Payments', href: '#solutions' },
    { name: 'Accounts', href: '#solutions' },
    { name: 'FX', href: '#solutions' }
  ],
  solutions: [
    { name: 'Corporates', href: '#solutions' },
    { name: 'Fintechs', href: '#solutions' },
    { name: 'E-commerce', href: '#industries' },
    { name: 'Gaming', href: '#industries' }
  ],
  company: [
    { name: 'About', href: '#company' },
    { name: 'Careers', href: '#' },
    { name: 'Contact', href: 'mailto:payments@easysendservices.com' }
  ],
  legal: [
    { name: 'Data Protection Addendum', href: '/data-protection-addendum' },
    { name: 'Privacy Policy', href: '/privacy-policy' },
    { name: 'Acceptable Use Policy', href: '/acceptable-use-policy' },
    { name: 'Financial Crime Risk Appetite', href: '/financial-crime-risk-appetite' }
  ]
};

export const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const scrollToSection = (hash) => {
    const element = document.querySelector(hash);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNavClick = (e, href) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      if (isHomePage) {
        scrollToSection(href);
      } else {
        navigate('/');
        setTimeout(() => scrollToSection(href), 300);
      }
    }
  };

  return (
    <footer 
      className="py-12 lg:py-16 bg-white border-t border-[#E5E7EB]"
      data-testid="footer"
    >
      <div className="section-container">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <a 
              href="/" 
              className="inline-block mb-6"
              onClick={(e) => {
                e.preventDefault();
                if (isHomePage) {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                  navigate('/');
                }
              }}
              data-testid="footer-logo"
            >
              <img 
                src={LOGO_URL} 
                alt="Easysend" 
                className="h-8 w-auto"
              />
            </a>
            <p className="text-sm text-[#6B7280] mb-6 max-w-xs">
              A regulated payment institution delivering reliable, transparent and compliant payment infrastructure.
            </p>
            <div className="space-y-2">
              <a 
                href="mailto:payments@easysendservices.com"
                className="block text-sm text-[#1E3A5F] hover:text-[#4FB3DF] transition-colors duration-200"
                data-testid="footer-email"
              >
                payments@easysendservices.com
              </a>
            </div>
          </div>

          {/* Platform Links */}
          <div>
            <h4 className="text-base font-semibold text-[#1E3A5F] mb-4">Platform</h4>
            <ul className="space-y-3">
              {footerLinks.platform.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className="text-sm text-[#6B7280] hover:text-[#1E3A5F] transition-colors duration-200"
                    data-testid={`footer-link-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Solutions Links */}
          <div>
            <h4 className="text-base font-semibold text-[#1E3A5F] mb-4">Solutions</h4>
            <ul className="space-y-3">
              {footerLinks.solutions.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className="text-sm text-[#6B7280] hover:text-[#1E3A5F] transition-colors duration-200"
                    data-testid={`footer-link-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-base font-semibold text-[#1E3A5F] mb-4">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className="text-sm text-[#6B7280] hover:text-[#1E3A5F] transition-colors duration-200"
                    data-testid={`footer-link-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-base font-semibold text-[#1E3A5F] mb-4">Legal</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-[#6B7280] hover:text-[#1E3A5F] transition-colors duration-200"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-16 pt-8 border-t border-[#E5E7EB]">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-[#6B7280]" data-testid="footer-copyright">
              © {new Date().getFullYear()} Easysend. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <span className="text-xs text-[#6B7280]">Regulated</span>
              <span className="w-1 h-1 bg-[#E5E7EB] rounded-full" />
              <span className="text-xs text-[#6B7280]">Transparent</span>
              <span className="w-1 h-1 bg-[#E5E7EB] rounded-full" />
              <span className="text-xs text-[#6B7280]">Reliable</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
